import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'

import { Observable, of } from 'rxjs'

import { AgentInstance } from '@flowx/designer/modules/agent-builder/models/agent.entity'
import { ApiHttpService } from '@flowx/designer/shared/api/api-http.service'

import { AgentExecutionPlan } from '../models/agent.entity'

@Injectable({ providedIn: 'root' })
export class AgentApiService extends ApiHttpService {
  #url = 'agent-builder/api/v1/workflows/executions'
  #urlModels = 'agent-builder/api/v1'
  #urlTests = 'agent-builder/api/v1/tests'

  constructor() {
    super(inject('BASE_URL' as any), inject(HttpClient))
  }

  runEvals(executionPlanId: AgentExecutionPlan['id']): Observable<any> {
    return this.post(`${this.#urlModels}/insights/runs/${executionPlanId}/evaluate`, {})
  }

  getExecutionEvals(executionPlanId: AgentExecutionPlan['id']): Observable<any> {
    return this.get(`${this.#urlModels}/insights/runs/execution/${executionPlanId}`)
  }

  getAgentOverallScore(
    wokflow_id: string,
    granulary: 'hour' | 'day' | 'week' | 'month' | 'run',
    from: string,
    to: string,
    environment: string
  ): Observable<any> {
    return this.get(`${this.#urlModels}/insights/agents/${wokflow_id}/overall-score`, {
      params: this.addParams({
        granularity: granulary,
        from,
        to,
        environment,
      }),
    })
  }

  getAgentDashboardSummary(
    wokflow_id: string,
    from: string,
    to: string,
    environment: string
  ): Observable<any> {
    return this.get(`${this.#urlModels}/insights/agents/${wokflow_id}/dashboard/summary`, {
      params: this.addParams({
        from,
        to,
        environment,
      }),
    })
  }

  getAgentDashboardTimeSeries(
    wokflow_id: string,
    granulary: 'hour' | 'day' | 'week' | 'month' | 'run',
    from: string,
    to: string,
    environment: string
  ): Observable<any> {
    return this.get(`${this.#urlModels}/insights/agents/${wokflow_id}/dashboard/timeseries`, {
      params: this.addParams({
        granularity: granulary,
        from,
        to,
        environment,
      }),
    })
  }

  getAgentDashboardMetrics(
    wokflow_id: string,
    from: string,
    to: string,
    environment: string
  ): Observable<any> {
    return this.get(`${this.#urlModels}/insights/agents/${wokflow_id}/dashboard/metrics`, {
      params: this.addParams({
        from,
        to,
        environment,
      }),
    })
  }
  getAgentDashboardAlerts(
    wokflow_id: string,
    from: string,
    to: string,
    environment: string
  ): Observable<any> {
    return this.get(`${this.#urlModels}/insights/agents/${wokflow_id}/dashboard/alerts`, {
      params: this.addParams({
        from,
        to,
        environment,
      }),
    })
  }
  getAgentDashboardCost(
    wokflow_id: string,
    from: string,
    to: string,
    environment: string
  ): Observable<any> {
    return this.get(`${this.#urlModels}/insights/agents/${wokflow_id}/dashboard/cost`, {
      params: this.addParams({
        from,
        to,
        environment,
      }),
    })
  }
  getAgentDashboardExecutions(
    wokflow_id: string,
    environment: string,
    limit: number = 20
  ): Observable<any> {
    return this.get(`${this.#urlModels}/insights/agents/${wokflow_id}/dashboard/executions`, {
      params: this.addParams({
        limit,
        environment,
      }),
    })
  }

  getAgentExecutions(workflow_id: string): Observable<AgentExecutionPlan[]> {
    return this.get(`${this.#url}/filtered/${workflow_id}`, {})
  }

  getAgentExecution(id: AgentInstance['id']): Observable<AgentExecutionPlan> {
    return this.get(`${this.#url}/${id}`)
  }

  getModels(): Observable<any> {
    const token = localStorage.getItem('access_token')
    if (token) {
      return this.get(`${this.#urlModels}/models`)
    }
    return of([])
  }
  getModelsParser(): Observable<any> {
    const token = localStorage.getItem('access_token')
    if (token) {
      return this.get(`${this.#urlModels}/models/parser`)
    }
    return of([])
  }

  getAddons(): Observable<any> {
    const token = localStorage.getItem('access_token')
    if (token) {
      return this.get(`${this.#urlModels}/addons`)
    }
    return of([])
  }
  getEntities(): Observable<any> {
    const token = localStorage.getItem('access_token')
    if (token) {
      return this.get(`${this.#urlModels}/data-privacy/privacy/entities`)
    }
    return of([])
  }

  // ── Test Datasets ────────────────────────────────────────────────────────

  getTestDatasets(workflowId: string): Observable<any[]> {
    return this.get(`${this.#urlTests}/${workflowId}/datasets`)
  }

  createTestDataset(
    workflowId: string,
    body: { name: string; description?: string }
  ): Observable<any> {
    return this.post(`${this.#urlTests}/${workflowId}/datasets`, body)
  }

  deleteTestDataset(datasetId: string): Observable<any> {
    return this.delete(`${this.#urlTests}/datasets/${datasetId}`)
  }

  // ── Test Cases ───────────────────────────────────────────────────────────

  getTestCases(datasetId: string): Observable<any[]> {
    return this.get(`${this.#urlTests}/datasets/${datasetId}/cases`)
  }

  saveExecutionAsTestCase(datasetId: string, executionId: string, name: string): Observable<any> {
    return this.post(
      `${this.#urlTests}/datasets/${datasetId}/cases/from-execution/${executionId}`,
      { name }
    )
  }

  deleteTestCase(caseId: string): Observable<any> {
    return this.delete(`${this.#urlTests}/cases/${caseId}`)
  }

  // ── Test Runs ────────────────────────────────────────────────────────────

  runAllTests(datasetId: string): Observable<any> {
    return this.post(`${this.#urlTests}/datasets/${datasetId}/run`, {})
  }

  runSingleTest(caseId: string): Observable<any> {
    return this.post(`${this.#urlTests}/cases/${caseId}/run`, {})
  }

  getTestRuns(datasetId: string): Observable<any[]> {
    return this.get(`${this.#urlTests}/datasets/${datasetId}/runs`)
  }

  getTestRun(runId: string): Observable<any> {
    return this.get(`${this.#urlTests}/runs/${runId}`)
  }
}
